import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function OwnerOverview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [msgStats, setMsgStats] = useState({ total: 0, unreplied: 0, pinned: 0 });
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadHealth = async () => {
    setHealthLoading(true);
    try {
      const { data } = await api.get("/admin/integrations-health");
      setHealth(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not check integrations");
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, u, m, h] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/messages").catch(() => ({ data: { stats: { total: 0, unreplied: 0, pinned: 0 } } })),
          api.get("/admin/integrations-health").catch(() => ({ data: null })),
        ]);
        setStats(s.data);
        setRecent(u.data.slice(0, 5));
        setMsgStats(m.data.stats || { total: 0, unreplied: 0, pinned: 0 });
        setHealth(h.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const chart = build30DayChart(stats?.signup_days || []);
  const chartMax = Math.max(1, ...chart.map((b) => b.count));

  if (loading || !stats) return <div className="text-[#5C6B61]">Loading…</div>;

  return (
    <section className="space-y-6" data-testid="owner-overview">
      <header>
        <div className="ff-label">Overview</div>
        <h1 className="text-3xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          What&apos;s happening
        </h1>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats">
        <Stat label="Total signups" value={stats.total_users} />
        <Stat label="New this week" value={stats.new_this_week} accent />
        <Stat label="New this month" value={stats.new_this_month} />
        <Stat label="Recipes saved" value={stats.total_recipes} />
        <Stat label="Shopping lists" value={stats.total_shopping_lists} />
        <Stat label="Food scans" value={stats.total_scans} />
        <Stat label="Google sign-ins" value={stats.google_users} />
        <Stat label="Email sign-ins" value={stats.jwt_users} />
      </div>

      <IntegrationsHealthCard health={health} loading={healthLoading} onRefresh={loadHealth} />

      <RecapTriggerCard />

      <div className="grid md:grid-cols-3 gap-3">
        <QuickLink to="/owner/messages" title="Customer messages" badge={msgStats.unreplied} icon="💬" subtitle={msgStats.unreplied > 0 ? `${msgStats.unreplied} need a reply` : "All caught up"} />
        <QuickLink to="/owner/users" title="Customers" icon="👥" subtitle={`${stats.total_users} total accounts`} />
        <QuickLink to="/owner/coupons" title="Coupons" icon="🎟️" subtitle="Create discount codes" />
      </div>

      <section className="ff-card p-6">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="ff-label">Signups · last 30 days</div>
            <p className="text-sm text-[#5C6B61] mt-1">
              {stats.new_this_month} new {stats.new_this_month === 1 ? "user" : "users"} in the past 30 days.
            </p>
          </div>
        </div>
        <div className="flex items-end gap-1 h-32" data-testid="signup-chart">
          {chart.map((b, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
              <div className="text-[10px] text-[#8B9690] opacity-0 group-hover:opacity-100 transition">
                {b.count > 0 ? b.count : ""}
              </div>
              <div
                className="w-full rounded-t bg-[#3B5A3E] hover:bg-[#2c4530] transition"
                style={{ height: `${(b.count / chartMax) * 100}%`, minHeight: b.count > 0 ? "4px" : "0" }}
                title={`${b.date}: ${b.count} signup${b.count === 1 ? "" : "s"}`}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-[#8B9690] mt-2">
          <span>{chart[0]?.date}</span>
          <span>{chart[chart.length - 1]?.date}</span>
        </div>
      </section>

      <section className="ff-card p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="ff-label">Most recent signups</div>
          <Link to="/owner/users" className="text-xs font-medium text-[#3B5A3E] hover:underline">See all →</Link>
        </div>
        <ul className="divide-y divide-[#F3F0EA]">
          {recent.map((u) => (
            <li key={u.user_id} className="py-3 flex items-center gap-3" data-testid="recent-signup-row">
              <Avatar user={u} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                <div className="text-xs text-[#8B9690] truncate">{u.email}</div>
              </div>
              <div className="text-xs text-[#8B9690] whitespace-nowrap">{formatDate(u.created_at)}</div>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}

const STATUS_STYLES = {
  ok: { bg: "bg-[#E8F0E9]", text: "text-[#3B5A3E]", dot: "bg-[#3B5A3E]", icon: "✓" },
  warn: { bg: "bg-[#FBE5DE]", text: "text-[#A65541]", dot: "bg-[#C36952]", icon: "!" },
  fail: { bg: "bg-[#FBE5DE]", text: "text-[#A33D26]", dot: "bg-[#A33D26]", icon: "✗" },
};

function IntegrationsHealthCard({ health, loading, onRefresh }) {
  const overall = health?.overall;
  const overallStyle = STATUS_STYLES[overall] || STATUS_STYLES.warn;
  return (
    <section className="ff-card p-6" data-testid="integrations-health-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F3F0EA] flex items-center justify-center text-xl flex-none">🩺</div>
          <div>
            <div className="ff-label">Integrations health</div>
            <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
              Live status of every 3rd-party service
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {health && (
            <span
              data-testid="integrations-overall-pill"
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${overallStyle.bg} ${overallStyle.text}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${overallStyle.dot}`} />
              {overall === "ok" ? "All systems go" : overall === "warn" ? "Needs attention" : "Action required"}
            </span>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            data-testid="integrations-refresh-btn"
            className="text-xs font-medium text-[#5C6B61] hover:text-[#2A332C] px-2.5 py-1 rounded-full hover:bg-[#F3F0EA] transition disabled:opacity-50"
          >
            {loading ? "Checking…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {!health ? (
        <p className="text-sm text-[#5C6B61] mt-4" data-testid="integrations-empty">
          {loading ? "Running checks…" : "No health data yet."}
        </p>
      ) : (
        <ul className="mt-5 space-y-2" data-testid="integrations-list">
          {health.checks.map((c) => (
            <IntegrationRow key={c.id} check={c} />
          ))}
        </ul>
      )}
    </section>
  );
}

function IntegrationRow({ check }) {
  const style = STATUS_STYLES[check.status] || STATUS_STYLES.warn;
  return (
    <li
      className="flex items-start gap-3 p-3 rounded-xl border border-[#F0EFE9] hover:bg-[#FAF9F6] transition"
      data-testid={`integration-row-${check.id}`}
    >
      <span
        className={`w-7 h-7 rounded-full ${style.bg} ${style.text} flex items-center justify-center text-sm font-semibold flex-none`}
        aria-hidden
      >
        {style.icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-[#2A332C]">{check.label}</span>
          <span className={`text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>
            {check.status}
          </span>
          {check.key_hint && (
            <span className="text-[11px] text-[#8B9690] font-mono" data-testid={`integration-key-${check.id}`}>
              {check.key_hint}
            </span>
          )}
        </div>
        <p className="text-xs text-[#5C6B61] mt-1 break-words">{check.message}</p>
      </div>
    </li>
  );
}

function RecapTriggerCard() {
  const [busy, setBusy] = useState(false);
  const [force, setForce] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/recap/send-now", { force });
      setResult(data);
      toast.success(
        data.sent > 0
          ? `Sent ${data.sent} recap${data.sent === 1 ? "" : "s"} (${data.skipped} skipped)`
          : `No emails sent — ${data.skipped} skipped`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not run recap batch");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ff-card p-6" data-testid="recap-trigger-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FBE5DE] flex items-center justify-center text-xl flex-none">📬</div>
          <div>
            <div className="ff-label">Weekly recap</div>
            <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Outfit, sans-serif" }}>
              Sunday digest emails
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-[#5C6B61] cursor-pointer">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="w-3.5 h-3.5 rounded accent-[#3B5A3E]"
              data-testid="recap-force-checkbox"
            />
            Force resend
          </label>
          <button
            onClick={send}
            disabled={busy}
            data-testid="recap-send-btn"
            className="rounded-full bg-[#3B5A3E] text-white text-xs font-medium px-4 py-2 hover:bg-[#2c4530] transition disabled:opacity-50"
          >
            {busy ? "Sending…" : "Send weekly recap"}
          </button>
        </div>
      </div>
      <p className="text-xs text-[#5C6B61] mt-3">
        Iterates over all Pro users with weekly recaps enabled, builds last week&apos;s digest, and emails it via Resend.
        Idempotent — already-sent users are skipped unless you tick <em>Force resend</em>.
      </p>
      {result && (
        <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-testid="recap-result">
          <Mini label="Sent" value={result.sent} accent="ok" />
          <Mini label="Skipped" value={result.skipped} accent="warn" />
          <Mini label="Failed" value={result.failed} accent={result.failed ? "fail" : "ok"} />
        </div>
      )}
    </section>
  );
}

function Mini({ label, value, accent }) {
  const style = STATUS_STYLES[accent] || STATUS_STYLES.warn;
  return (
    <div className={`rounded-xl p-3 ${style.bg}`}>
      <div className={`text-[10px] uppercase tracking-wider font-medium ${style.text}`}>{label}</div>
      <div className={`text-xl font-semibold ${style.text}`} style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`ff-card p-4 ${accent ? "ring-1 ring-[#3B5A3E]/20" : ""}`}>
      <div className="ff-label">{label}</div>
      <div className="text-2xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
        {value ?? 0}
      </div>
    </div>
  );
}

function QuickLink({ to, title, subtitle, icon, badge }) {
  return (
    <Link to={to} className="ff-card p-4 hover:shadow-md transition flex items-start gap-3" data-testid={`quicklink-${to.split("/").pop()}`}>
      <div className="w-10 h-10 rounded-xl bg-[#F3F0EA] flex items-center justify-center text-xl flex-none">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#2A332C] flex items-center gap-2">
          {title}
          {badge ? (
            <span className="bg-[#A33D26] text-white text-[10px] font-medium px-2 py-0.5 rounded-full">{badge}</span>
          ) : null}
        </div>
        <div className="text-xs text-[#5C6B61] mt-0.5">{subtitle}</div>
      </div>
    </Link>
  );
}

function Avatar({ user }) {
  if (user.picture) return <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-none" />;
  const initials = (user.name || user.email || "?").slice(0, 1).toUpperCase();
  return <div className="w-8 h-8 rounded-full bg-[#3B5A3E] text-white flex items-center justify-center text-xs font-semibold flex-none">{initials}</div>;
}

function formatDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function build30DayChart(signupDays) {
  const map = new Map((signupDays || []).map((d) => [d.date, d.count]));
  const out = [];
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({ date: key, count: Number(map.get(key) || 0) });
  }
  return out;
}
